/**
 * `.scrollTransition`: a box's effects drawn as it moves through its scroll
 * view, from the three phases the app sends (`ScrollTransition`), without a
 * round trip. After every layout and once per animation frame while
 * anything scrolls, each box's progress (how far out of the scroll view's
 * visible area it is, as a share of its own length, negative past the top
 * or leading edge) picks the effects: `interactive` between the identity
 * phase and the edge's by that share, `animated` the edge's (CSS-animated)
 * once partly out, `identity` the identity phase's.
 *
 * Positions are the boxes' laid-out ones (offsets, not client rects), so a
 * transition's own offset never feeds back into its progress.
 */

import type { ScrollTransition, ScrollTransitionEffect, Style } from './protocol';
import { effectsCSS } from './styles';

interface Registered {
  spec: ScrollTransition;
  /** The box's own effects, which the transition's come after. */
  base: { transform: string; filter: string; opacity: number };
}

const registered = new Map<HTMLElement, Registered>();

const ANIMATED = 'transform 0.35s ease, opacity 0.35s ease, filter 0.35s ease';

/** Called by `applyStyle` for every `styled` box: registers, updates or forgets its transition. */
export function setScrollTransition(el: HTMLElement, style: Style): void {
  const spec = style.scrollTransition;
  if (!spec) {
    if (registered.delete(el)) {
      delete el.dataset['sbScrollTransition'];
      el.style.transition = '';
    }
    return;
  }
  // Its shadow is cast by the views it draws (renderer.ts castShadows), not by it.
  const own = effectsCSS(style, { shadow: false });
  registered.set(el, {
    spec,
    base: { transform: own.transform ?? '', filter: own.filter ?? '', opacity: style.opacity ?? 1 },
  });
  el.dataset['sbScrollTransition'] = spec.configuration;
  el.style.transition = spec.configuration === 'animated' ? ANIMATED : '';
  draw(el);
}

/** `from` → `to` by `t`, field by field (absent fields are the identity's). */
export function interpolateEffect(from: ScrollTransitionEffect, to: ScrollTransitionEffect, t: number): ScrollTransitionEffect {
  const mix = (a: number, b: number): number => a + (b - a) * t;
  const out: ScrollTransitionEffect = {};
  const ox = mix(from.offset?.x ?? 0, to.offset?.x ?? 0);
  const oy = mix(from.offset?.y ?? 0, to.offset?.y ?? 0);
  if (ox !== 0 || oy !== 0) out.offset = { x: ox, y: oy };
  const sx = mix(from.scale?.x ?? 1, to.scale?.x ?? 1);
  const sy = mix(from.scale?.y ?? 1, to.scale?.y ?? 1);
  if (sx !== 1 || sy !== 1) out.scale = { x: sx, y: sy };
  const scaleAnchor = to.scaleAnchor ?? from.scaleAnchor;
  if (scaleAnchor) out.scaleAnchor = scaleAnchor;
  const rotation = mix(from.rotation ?? 0, to.rotation ?? 0);
  if (rotation !== 0) out.rotation = rotation;
  const rotationAnchor = to.rotationAnchor ?? from.rotationAnchor;
  if (rotationAnchor) out.rotationAnchor = rotationAnchor;
  const r3 = to.rotation3D ?? from.rotation3D;
  if (r3) out.rotation3D = { ...r3, angle: mix(from.rotation3D?.angle ?? 0, to.rotation3D?.angle ?? 0) };
  const opacity = mix(from.opacity ?? 1, to.opacity ?? 1);
  if (opacity !== 1) out.opacity = opacity;
  const blur = mix(from.blur ?? 0, to.blur ?? 0);
  if (blur !== 0) out.blur = blur;
  const brightness = mix(from.brightness ?? 0, to.brightness ?? 0);
  if (brightness !== 0) out.brightness = brightness;
  const contrast = mix(from.contrast ?? 1, to.contrast ?? 1);
  if (contrast !== 1) out.contrast = contrast;
  const saturation = mix(from.saturation ?? 1, to.saturation ?? 1);
  if (saturation !== 1) out.saturation = saturation;
  const grayscale = mix(from.grayscale ?? 0, to.grayscale ?? 0);
  if (grayscale !== 0) out.grayscale = grayscale;
  const hueRotation = mix(from.hueRotation ?? 0, to.hueRotation ?? 0);
  if (hueRotation !== 0) out.hueRotation = hueRotation;
  return out;
}

/** The effects at `progress` (see the file comment). */
export function effectAt(spec: ScrollTransition, progress: number): ScrollTransitionEffect {
  if (spec.configuration === 'identity' || progress === 0) return spec.identity;
  const edge = progress < 0 ? spec.topLeading : spec.bottomTrailing;
  if (spec.configuration === 'animated') return edge;
  return interpolateEffect(spec.identity, edge, Math.min(1, Math.abs(progress)));
}

/** Where `el` is laid out inside `scroller`'s content (offsets: transforms left out). */
export function layoutOrigin(el: HTMLElement, scroller: HTMLElement): { x: number; y: number } | null {
  let x = 0;
  let y = 0;
  let node: HTMLElement | null = el;
  while (node && node !== scroller) {
    x += node.offsetLeft;
    y += node.offsetTop;
    const parent = node.offsetParent as HTMLElement | null;
    if (!parent || !scroller.contains(parent)) return parent === scroller ? { x, y } : null;
    node = parent;
  }
  return { x, y };
}

/** How far `el` is out of its scroll view's visible area (0 when it is not in one). */
export function progressOf(el: HTMLElement, axis?: 'horizontal' | 'vertical'): number {
  const scroller = el.parentElement?.closest<HTMLElement>('.sb-scrollview, .sb-list');
  if (!scroller) return 0;
  const origin = layoutOrigin(el, scroller);
  if (!origin) return 0;
  const horizontal = (axis ?? (scroller.dataset['sbAxes'] === 'horizontal' ? 'horizontal' : 'vertical')) === 'horizontal';
  const length = horizontal ? el.offsetWidth : el.offsetHeight;
  if (length <= 0) return 0;
  const start = horizontal ? origin.x : origin.y;
  const viewStart = horizontal ? scroller.scrollLeft : scroller.scrollTop;
  const viewEnd = viewStart + (horizontal ? scroller.clientWidth : scroller.clientHeight);
  if (start < viewStart - 0.5) return -Math.min(1, (viewStart - start) / length);
  if (start + length > viewEnd + 0.5) return Math.min(1, (start + length - viewEnd) / length);
  return 0;
}

function draw(el: HTMLElement): void {
  const entry = registered.get(el);
  if (!entry) return;
  const effect = effectAt(entry.spec, el.isConnected ? progressOf(el, entry.spec.axis) : 0);
  const css = effectsCSS(effect);
  const s = el.style;
  s.transform = [css.transform, entry.base.transform].filter(Boolean).join(' ');
  if (css.transformOrigin) s.transformOrigin = css.transformOrigin;
  s.filter = [entry.base.filter, css.filter].filter(Boolean).join(' ');
  const opacity = entry.base.opacity * (effect.opacity ?? 1);
  s.opacity = opacity === 1 ? '' : String(Math.round(opacity * 1000) / 1000);
}

/** Draws every transition at its box's current place: after a layout, and per frame while scrolling. */
export function drawScrollTransitions(): void {
  for (const el of registered.keys()) {
    if (!el.isConnected) {
      registered.delete(el);
      continue;
    }
    draw(el);
  }
}
